/**
 * tokenRouter.js
 * ─────────────────────────────────────────────────────────────
 * Mirrors the PHP token routing logic from index.php
 *
 * Usage:
 *   import { resolveToken } from './tokenRouter';
 *   const result = resolveToken('MSGabcdef1234567890');
 *
 * To wire up the real API instead of local resolution, replace
 * resolveToken with resolveTokenFromAPI (also exported below).
 * ─────────────────────────────────────────────────────────────
 */

const BASE = 'https://mygradecard.online/app';

/** All known prefix → URL builders */
export const PREFIX_MAP = [
  {
    code: 'PVC',
    label: 'Pickup verification card',
    build: (code, extra = '') =>
      `${BASE}/modules/SECURITY_VERIFICATION/verify_pc.php?x=${code}${extra}`,
  },
  {
    code: 'QRR',
    label: 'QR result verification',
    build: (code) => `${BASE}/resultchecker/verifyResultQR.php?x=${code}`,
  },
  {
    code: 'LAS',
    label: 'LMS public VASS',
    build: (code) => `${BASE}/lms/app/public_vass.php?x=${code}`,
  },
  {
    code: 'MSG',
    label: 'eMessage',
    build: (code, extra = '') => `${BASE}/emessage/?x=${code}${extra}`,
  },
  {
    code: 'SMTR',
    label: 'SMR module (alt)',
    build: (code, extra = '') => `${BASE}/smr/?x=${code}${extra}`,
  },
  {
    code: 'SMR',
    label: 'SMR module',
    build: (code, extra = '') => `${BASE}/smr/?x=${code}${extra}`,
  },
  {
    code: 'RBR',
    label: 'eRubric',
    build: (code) => `${BASE}/erubric/?x=${code}`,
  },
  {
    code: 'INV',
    label: 'Invoice',
    build: (code) => `${BASE}/invoice/?x=${code}`,
  },
  {
    code: 'ASR',
    label: 'ASR linker',
    build: (code) => `${BASE}/linkasr/?x=${code}`,
  },
];

/**
 * Sanitise raw input — mirrors PHP preg_replace + strip_tags
 * Strips everything except A-Z a-z 0-9
 */
export function sanitiseToken(raw = '') {
  return raw.replace(/[^A-Za-z0-9]/g, '');
}

/**
 * resolveToken(raw, extraParams?)
 *
 * @param {string} raw          - The raw token string (unsanitised is fine)
 * @param {string} extraParams  - Optional extra query string e.g. "&foo=bar"
 * @returns {{ ref, code, url, label, fallback?, error? }}
 */
export function resolveToken(raw = '', extraParams = '') {
  const token = sanitiseToken(raw);

  if (!token) {
    return { error: 'Empty token — nothing to route.' };
  }

  // Check 4-char prefix first (SMTR before SMR)
  for (const entry of PREFIX_MAP) {
    const len = entry.code.length;
    if (token.slice(0, len).toUpperCase() === entry.code) {
      const code = token.slice(len);
      return {
        ref: entry.code,
        code,
        label: entry.label,
        url: entry.build(code, extraParams),
      };
    }
  }

  // Fallback — no known prefix
  // PHP would do a DB lookup for school username here.
  // Since we can't query the DB on the client, we surface both possibilities.
  return {
    ref: '—',
    code: token,
    label: 'Unknown prefix',
    url: `${BASE}/resultchecker/${token}${extraParams}`,
    fallback: true,
    fallbackNote:
      'No recognised prefix. PHP would attempt a school DB lookup first, then redirect to the result checker.',
  };
}

/**
 * resolveTokenFromAPI(raw, extraParams?)
 *
 * Drop-in async replacement for resolveToken once your API is ready.
 * Swap import in TokenRouter.jsx:
 *   import { resolveTokenFromAPI as resolveToken } from '../utils/tokenRouter';
 *
 * Expected API response shape:
 * {
 *   ref:   "MSG",
 *   code:  "abcdef1234567890",
 *   label: "eMessage",
 *   url:   "https://mygradecard.online/app/emessage/?x=abcdef1234567890",
 *   fallback?: boolean,
 *   fallbackNote?: string,
 *   error?: string
 * }
 */
export async function resolveTokenFromAPI(raw = '', extraParams = '') {
  const token = sanitiseToken(raw);
  if (!token) return { error: 'Empty token — nothing to route.' };

  try {
    // 🔌 Replace this URL with your actual API endpoint
    const res = await fetch(`/api/resolve-token?token=${token}${extraParams}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    // Graceful degradation — fall back to local resolution
    console.warn('API unavailable, using local resolver:', err.message);
    return resolveToken(raw, extraParams);
  }
}
